import {
  CAD_STOCK_STATUS_ITEMS,
  CAD_USES,
  CAD_USE_LABELS,
} from './cadConstants';

/**
 * Filtros de la lista CAD (`/#/lista/:id`).
 * Mismo gesto que la lista compartida; **otro contrato**.
 * Parse: `context: "cad-list"` → `schema_version: "cad-list-v1"`.
 *
 * Keys canónicas = las del API (`FRONTEND_PARSE_SEARCH_QUERY.md`):
 * `surface` (bandas sobre `area_m2`), `cad_price` `{min,max}`.
 *
 * @see docs/maps/cad-list-filters.md
 */

export const CAD_FILTER_SCHEMA_VERSION = 'cad-list-v1';
export const CAD_PARSE_CONTEXT = 'cad-list';

/** “En venta” / “a la venta”: se puede comprar. No es `listing_operation`. */
export const CAD_FOR_SALE_STOCK_STATUSES = ['available', 'offer'];

export const CAD_FILTER_CURRENCIES = ['USD', 'ARS', 'EUR'];

/**
 * Bandas de superficie: mismas keys que el tab Lote (`surface`),
 * evaluadas sobre `area_m2` de la ficha.
 */
export const CAD_AREA_BANDS = [
  { key: 'lt200', value: 'lt200', label: '< 200 m²', shortLabel: '< 200', min: null, max: 200, maxExclusive: true },
  { key: '200-500', value: '200-500', label: '200–500 m²', shortLabel: '200–500', min: 200, max: 500 },
  { key: '500-1000', value: '500-1000', label: '500–1.000 m²', shortLabel: '500–1k', min: 500, max: 1000 },
  { key: '1000-5000', value: '1000-5000', label: '1.000–5.000 m²', shortLabel: '1k–5k', min: 1000, max: 5000 },
  { key: '5k_10k', value: '5k_10k', label: '5.000–10.000 m²', shortLabel: '5k–10k', min: 5000, max: 10000, maxExclusive: true },
  { key: '1_5ha', value: '1_5ha', label: '1–5 ha', shortLabel: '1–5 ha', min: 10000, max: 50000, maxExclusive: true },
  { key: '5_10ha', value: '5_10ha', label: '5–10 ha', shortLabel: '5–10 ha', min: 50000, max: 100000, maxExclusive: true },
  { key: '10_20ha', value: '10_20ha', label: '10–20 ha', shortLabel: '10–20 ha', min: 100000, max: 200000, maxExclusive: true },
  { key: '20_50ha', value: '20_50ha', label: '20–50 ha', shortLabel: '20–50 ha', min: 200000, max: 500000, maxExclusive: true },
  { key: '50_100ha', value: '50_100ha', label: '50–100 ha', shortLabel: '50–100 ha', min: 500000, max: 1000000, maxExclusive: true },
  { key: 'gte100ha', value: 'gte100ha', label: '≥ 100 ha', shortLabel: '≥ 100 ha', min: 1000000, max: null },
];

/**
 * Min/max de `area_m2` del GeoJSON de este proyecto.
 * @param {object[]|null|undefined} features
 * @returns {{ min: number, max: number }|null}
 */
export function getCadAreaExtent(features) {
  let min = Infinity;
  let max = -Infinity;
  (Array.isArray(features) ? features : []).forEach((feature) => {
    const value = Number(feature?.properties?.area_m2);
    if (!Number.isFinite(value) || value < 0) return;
    if (value < min) min = value;
    if (value > max) max = value;
  });
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

function bandOverlapsExtent(band, min, max) {
  const lo = band.min == null ? Number.NEGATIVE_INFINITY : band.min;
  const hi = band.max == null ? Number.POSITIVE_INFINITY : band.max;
  if (band.maxExclusive) return lo <= max && hi > min;
  return lo <= max && hi >= min;
}

/**
 * Bandas que tocan el rango de la lista + la siguiente (vacía) para
 * que se vea el techo. Sin extent → catálogo completo.
 * @param {{ min: number, max: number }|null|undefined} extent
 * @param {typeof CAD_AREA_BANDS} [bands]
 */
export function getCadSurfaceBandsForExtent(extent, bands = CAD_AREA_BANDS) {
  if (!extent || !Number.isFinite(extent.min) || !Number.isFinite(extent.max)) {
    return bands;
  }
  const { min, max } = extent;
  const overlapping = [];
  let lastIndex = -1;
  bands.forEach((band, index) => {
    if (!bandOverlapsExtent(band, min, max)) return;
    overlapping.push(band);
    lastIndex = index;
  });
  if (!overlapping.length) return bands;
  if (lastIndex >= 0 && lastIndex < bands.length - 1) {
    return [...overlapping, bands[lastIndex + 1]];
  }
  return overlapping;
}

/**
 * Grupos del panel. `surfaceBands` recorta chips; el parse sigue emitiendo
 * las keys del catálogo. `omitFields` saca del panel lo que en esa vista ya
 * tiene su propio control (en Listas, el estado vive en el detalle).
 * @param {typeof CAD_AREA_BANDS} [surfaceBands]
 * @param {{ omitFields?: string[] }} [options]
 */
export function buildCadFilterGroups(surfaceBands = CAD_AREA_BANDS, { omitFields = [] } = {}) {
  const options = Array.isArray(surfaceBands) && surfaceBands.length
    ? surfaceBands
    : CAD_AREA_BANDS;
  return [
    {
      id: 'cad_list',
      label: 'Lotes',
      defaultOpen: true,
      accordion: false,
      filters: CAD_FILTER_DEFS
        .filter((def) => !omitFields.includes(def.field))
        .map((def) => (
          def.field === 'surface'
            ? { ...def, options, columns: Math.min(options.length, 4) }
            : def
        )),
    },
  ];
}

const stockOptions = CAD_STOCK_STATUS_ITEMS.map(({ id, label }) => ({
  key: id,
  value: id,
  label,
}));

const useOptions = CAD_USES.map((value) => ({
  key: value,
  value,
  label: CAD_USE_LABELS[value] || value,
}));

/**
 * Defs que `setFilterValue` de esta vista entiende.
 * El parse emite estas `field` y estos `value`.
 */
export const CAD_FILTER_DEFS = [
  {
    id: 'cad_search',
    component: 'textContains',
    field: 'cad_search',
    label: 'Buscar en la lista',
    shortLabel: 'Búsqueda',
    placeholder: 'Nombre, descripción…',
    minLength: 2,
  },
  {
    id: 'stock_status',
    component: 'multiSelect',
    field: 'stock_status',
    label: 'Estado',
    options: stockOptions,
  },
  {
    id: 'use',
    component: 'multiSelect',
    field: 'use',
    label: 'Uso',
    options: useOptions,
  },
  {
    id: 'surface',
    component: 'rangeBands',
    field: 'surface',
    dbField: 'area_m2',
    label: 'Superficie',
    options: CAD_AREA_BANDS,
  },
  {
    id: 'currency',
    component: 'multiSelect',
    field: 'currency',
    label: 'Moneda',
    options: CAD_FILTER_CURRENCIES.map((value) => ({ key: value, value, label: value })),
  },
  {
    id: 'cad_price',
    component: 'range',
    field: 'cad_price',
    dbField: 'price',
    label: 'Precio',
    hint: 'Número de la ficha. Sin cuartiles en v1.',
  },
];

export const CAD_FILTER_KEYS = CAD_FILTER_DEFS.map((def) => def.id);

export const CAD_FILTER_GROUPS = buildCadFilterGroups();

/** Alias del parse / defs viejas → field canónico. */
const CAD_FILTER_ALIASES = {
  area_m2: 'surface',
  price: 'cad_price',
};

/** Keys de catastro / share que este schema nunca aplica. */
export const CAD_FILTER_FORBIDDEN_KEYS = [
  'listing_operation',
  'listing_price',
  'listing_currency',
  'listing_search',
  'listing_price_bands',
  'type',
  'state',
  'province_name',
  'neighborhood_name',
  'street_name',
];

/**
 * Sinónimos de frase → `stock_status`. El parse resuelve y emite el array.
 */
export const CAD_STOCK_NL_SYNONYMS = {
  'en venta': CAD_FOR_SALE_STOCK_STATUSES,
  'a la venta': CAD_FOR_SALE_STOCK_STATUSES,
  'para comprar': CAD_FOR_SALE_STOCK_STATUSES,
  disponibles: ['available'],
  disponible: ['available'],
  'en oferta': ['offer'],
  oferta: ['offer'],
  reservados: ['reserved'],
  reservado: ['reserved'],
  vendidos: ['sold'],
  vendido: ['sold'],
  'no disponible': ['unavailable'],
};

function hasCadFilterValue(value, def) {
  if (value == null) return false;
  if (def?.component === 'multiSelect' || def?.component === 'rangeBands') {
    return Array.isArray(value) && value.length > 0;
  }
  if (def?.component === 'textContains') {
    return typeof value === 'string' && value.trim().length >= (def.minLength || 1);
  }
  if (def?.component === 'range') {
    if (typeof value !== 'object' || Array.isArray(value)) return false;
    const min = Number(value.min);
    const max = Number(value.max);
    return Number.isFinite(min) || Number.isFinite(max);
  }
  return false;
}

function formatCadPriceChip(range) {
  const min = Number(range?.min);
  const max = Number(range?.max);
  const fmt = (n) => n.toLocaleString('es-AR', { maximumFractionDigits: 0 });
  if (Number.isFinite(min) && Number.isFinite(max)) return `${fmt(min)}–${fmt(max)}`;
  if (Number.isFinite(min)) return `desde ${fmt(min)}`;
  if (Number.isFinite(max)) return `hasta ${fmt(max)}`;
  return '';
}

/**
 * Chips del panel CAD. Misma forma que `getAppliedFilterChips`.
 * @param {Record<string, unknown>} filters
 */
export function getCadAppliedFilterChips(filters) {
  const chips = [];
  CAD_FILTER_DEFS.forEach((def) => {
    const value = filters?.[def.field];
    if (!hasCadFilterValue(value, def)) return;
    const fieldLabel = def.shortLabel || def.label;
    if (def.component === 'multiSelect' || def.component === 'rangeBands') {
      const options = def.options || [];
      value.forEach((item) => {
        const opt = options.find((option) => option.value === item);
        chips.push({
          id: `${def.field}-${item}`,
          field: def.field,
          label: `${fieldLabel}: ${opt?.shortLabel || opt?.label || item}`,
          removeValue: item,
        });
      });
      return;
    }
    if (def.component === 'textContains') {
      chips.push({
        id: def.field,
        field: def.field,
        label: `${fieldLabel}: ${String(value).trim()}`,
        removeValue: null,
      });
      return;
    }
    if (def.component === 'range') {
      const text = formatCadPriceChip(value);
      if (!text) return;
      chips.push({
        id: def.field,
        field: def.field,
        label: `${fieldLabel}: ${text}`,
        removeValue: null,
      });
    }
  });
  return chips;
}

/**
 * @param {Record<string, unknown>} filters
 * @returns {number}
 */
export function countCadActiveFilters(filters) {
  return CAD_FILTER_DEFS.reduce(
    (count, def) => (hasCadFilterValue(filters?.[def.field], def) ? count + 1 : count),
    0,
  );
}

export function isCadFilterValueActive(value, def) {
  return hasCadFilterValue(value, def);
}

/**
 * Quita keys ajenas y normaliza aliases (`area_m2` → `surface`, `price` → `cad_price`).
 * @param {object|null|undefined} filters
 * @returns {object}
 */
export function canonicalizeCadListFilters(filters) {
  const source = filters && typeof filters === 'object' ? filters : {};
  const remapped = {};
  Object.entries(source).forEach(([key, value]) => {
    const field = CAD_FILTER_ALIASES[key] || key;
    remapped[field] = value;
  });
  return remapped;
}
